<template>
  <div class="profile container">
    <!-- Not logged in (edge case: route guard should normally prevent this) -->
    <div v-if="!user" class="profile__guard reveal">
      <div class="profile__peel">🍌🔒</div>
      <h1>Tu n'es pas connecté</h1>
      <p>Connecte-toi pour retrouver ton profil et tes blagues favorites.</p>
      <BananaButton to="/login" size="lg">Se connecter</BananaButton>
    </div>

    <template v-else>
      <!-- Header -->
      <header class="profile__head reveal">
        <div class="profile__avatar" aria-hidden="true">{{ initial }}</div>
        <div class="profile__id">
          <h1 class="profile__name">{{ user.displayName || pseudoFromEmail }}</h1>
          <p class="profile__email">{{ user.email }}</p>
          <p v-if="joinedLabel" class="profile__joined">Membre depuis {{ joinedLabel }}</p>
        </div>
        <BananaButton variant="danger" size="sm" class="profile__logout" @click="doLogout">
          Déconnexion
        </BananaButton>
      </header>

      <!-- Favorites -->
      <section class="profile__section">
        <h2 class="profile__section-title">⭐ Mes favoris</h2>

        <div v-if="favLoading && !favorites.length" class="profile__state">
          <span class="spin">🍌</span> On épluche tes favoris…
        </div>

        <div v-else-if="!favorites.length" class="profile__empty">
          <div class="profile__peel">🍌</div>
          <p>Aucun favori pour l'instant. Ajoute des blagues à tes favoris depuis leur page !</p>
          <BananaButton to="/" variant="dark" size="sm">Parcourir les blagues</BananaButton>
        </div>

        <template v-else>
          <ul class="fav-list">
            <li v-for="joke in favorites" :key="joke.id" class="fav-card">
              <router-link :to="`/item/${joke.id}`" class="fav-card__link">
                <p class="fav-card__text">{{ joke.text }}</p>
                <div class="fav-card__meta">
                  <span v-if="joke.authorName" class="fav-card__author">✍️ {{ joke.authorName }}</span>
                  <span class="fav-card__score">🍌 {{ joke.score }}</span>
                </div>
              </router-link>
            </li>
          </ul>

          <div v-if="canLoadMore" class="fav-more">
            <BananaButton variant="outline" :loading="favLoading" @click="loadMore">
              Voir plus
            </BananaButton>
          </div>
        </template>
      </section>
    </template>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import BananaButton from '@/components/ui/BananaButton.vue'
import { MY_FAVORITES_QUERY } from '@/graphql/social'
import { authState, logout } from '@/auth'

const PAGE_SIZE = 10

// Local query to grab the registration date, which isn't stored in
// authState.user (that only holds id/email/displayName/isStaff).
const ME_JOINED = gql`
  query MeJoined {
    me {
      id
      dateJoined
    }
  }
`

export default {
  name: 'ProfileView',
  components: { BananaButton },
  data() {
    return {
      authState,
      favorites: [],
      favLoading: true,
      offset: 0,
      canLoadMore: false,
      dateJoined: null,
    }
  },
  computed: {
    user() {
      return this.authState.user
    },
    initial() {
      const src = this.user?.displayName || this.user?.email || '?'
      return src.trim().charAt(0).toUpperCase() || '?'
    },
    pseudoFromEmail() {
      return (this.user?.email || '').split('@')[0] || 'Banané·e'
    },
    joinedLabel() {
      if (!this.dateJoined) return ''
      const d = new Date(this.dateJoined)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })
    },
  },
  apollo: {
    me: {
      query: ME_JOINED,
      skip() { return !this.user },
      update(data) {
        if (data && data.me) this.dateJoined = data.me.dateJoined
        return data && data.me
      },
    },
  },
  created() {
    if (this.user) this.fetchFavorites()
    else this.favLoading = false
  },
  methods: {
    async fetchFavorites() {
      this.favLoading = true
      try {
        const { data } = await this.$apollo.query({
          query: MY_FAVORITES_QUERY,
          variables: { count: PAGE_SIZE, offset: this.offset },
          fetchPolicy: 'network-only',
        })
        const batch = (data && data.myFavorites) || []
        this.favorites = this.offset === 0 ? batch : this.favorites.concat(batch)
        this.canLoadMore = batch.length === PAGE_SIZE
      } catch (e) {
        console.error('Error fetching favorites:', e)
        this.canLoadMore = false
      } finally {
        this.favLoading = false
      }
    },
    loadMore() {
      this.offset += PAGE_SIZE
      this.fetchFavorites()
    },
    doLogout() {
      logout()
      this.$router.push('/')
    },
  },
}
</script>

<style scoped>
.profile { padding: 40px 20px 70px; max-width: 760px; }

/* Header */
.profile__head {
  display: flex;
  align-items: center;
  gap: 20px;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-xl);
  box-shadow: var(--shadow-pop);
  padding: 24px 26px;
  margin-bottom: 36px;
  flex-wrap: wrap;
}
.profile__avatar {
  flex: 0 0 auto;
  width: 68px; height: 68px;
  display: grid; place-items: center;
  font-family: var(--font-display);
  font-weight: 700; font-size: 2rem;
  color: var(--ink-900);
  background: var(--banana-500);
  border: var(--border-w) solid var(--border-c);
  border-radius: 50%;
}
.profile__id { flex: 1 1 200px; min-width: 0; }
.profile__name { font-size: 1.5rem; margin: 0 0 2px; word-break: break-word; }
.profile__email { color: var(--text-soft); margin: 0; font-size: 0.95rem; word-break: break-word; }
.profile__joined {
  margin: 6px 0 0; font-size: 0.82rem; color: var(--text-soft);
  font-family: var(--font-accent); font-style: italic;
}
.profile__logout { flex: 0 0 auto; }

/* Section */
.profile__section-title { font-size: 1.3rem; margin: 0 0 18px; }

/* States */
.profile__state {
  text-align: center; padding: 40px; color: var(--text-soft);
  font-family: var(--font-accent); font-style: italic; font-size: 1.05rem;
}
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.profile__empty {
  text-align: center; padding: 34px 20px;
  border: 2px dashed color-mix(in srgb, var(--ink-900) 25%, transparent);
  border-radius: var(--r-lg);
}
.profile__empty p { color: var(--text-soft); margin: 0 auto 18px; max-width: 400px; }
.profile__peel { font-size: 2.6rem; margin-bottom: 10px; }

/* Favorites list */
.fav-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
.fav-card {
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-pop);
  transition: transform 0.15s var(--ease-out), box-shadow 0.15s var(--ease-out);
}
.fav-card:hover { transform: translate(-2px, -2px); box-shadow: 6px 6px 0 var(--border-c); }
.fav-card__link { display: block; padding: 16px 20px; text-decoration: none; color: var(--ink-900); }
.fav-card__text {
  margin: 0 0 10px; font-size: 1.02rem; line-height: 1.5;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.fav-card__meta { display: flex; gap: 14px; flex-wrap: wrap; font-size: 0.85rem; font-weight: 600; color: var(--text-soft); }

.fav-more { text-align: center; margin-top: 24px; }

/* Guard */
.profile__guard { text-align: center; padding: 60px 20px; }
.profile__guard h1 { font-size: clamp(1.7rem, 5vw, 2.4rem); }
.profile__guard p { color: var(--text-soft); max-width: 420px; margin: 0 auto 24px; }

@media (max-width: 560px) {
  .profile { padding: 30px 20px 50px; }
  .profile__head { padding: 20px; }
  .profile__logout { width: 100%; }
}
</style>
